/* ============================================
   USE BOOKMARK HOOK
   The Creative Orchestra - GEMINI Framework
   Manages bookmark/resume reading functionality
   ============================================ */

import { useState, useEffect, useCallback } from 'react';
import { getStorageItem, setStorageItem, removeStorageItem } from '@utils/helpers';
import { STORAGE_KEYS } from '@utils/constants';

/**
 * Bookmark data structure
 * @typedef {Object} BookmarkData
 * @property {string} chapterId - Chapter ID (e.g., '01', '02')
 * @property {number} scrollPosition - Scroll position in pixels
 * @property {number} scrollPercentage - Scroll percentage (0-100)
 * @property {string} timestamp - ISO timestamp of bookmark
 * @property {string} chapterTitle - Chapter title for display
 */

/**
 * Custom hook for managing novel reading bookmarks
 * Persists reading position to localStorage
 * 
 * @returns {Object} Bookmark state and actions
 */
const useBookmark = () => {
  // State
  const [bookmark, setBookmark] = useState(null);
  const [visitedChapters, setVisitedChapters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Load bookmark from localStorage on mount
   */
  useEffect(() => {
    const loadBookmark = () => {
      try {
        const savedBookmark = getStorageItem(STORAGE_KEYS.bookmark, null);
        const savedVisited = getStorageItem(STORAGE_KEYS.visitedChapters, []);
        
        setBookmark(savedBookmark);
        setVisitedChapters(savedVisited);
      } catch (error) {
        console.warn('Failed to load bookmark:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBookmark();
  }, []);

  /**
   * Save bookmark to localStorage
   * @param {string} chapterId - Chapter ID to bookmark
   * @param {Object} options - Additional options
   * @param {number} options.scrollPosition - Current scroll position
   * @param {number} options.scrollPercentage - Current scroll percentage
   * @param {string} options.chapterTitle - Chapter title
   */
  const saveBookmark = useCallback((chapterId, options = {}) => {
    const bookmarkData = {
      chapterId,
      scrollPosition: options.scrollPosition || 0,
      scrollPercentage: options.scrollPercentage || 0,
      chapterTitle: options.chapterTitle || `Chapter ${chapterId}`,
      timestamp: new Date().toISOString(),
    };

    setBookmark(bookmarkData);
    setStorageItem(STORAGE_KEYS.bookmark, bookmarkData);

    // Also mark chapter as visited
    markChapterVisited(chapterId);

    return bookmarkData;
  }, []);

  /**
   * Update bookmark scroll position without changing chapter
   * @param {number} scrollPosition - New scroll position
   * @param {number} scrollPercentage - New scroll percentage
   */
  const updateScrollPosition = useCallback((scrollPosition, scrollPercentage) => {
    if (!bookmark) return;

    const updatedBookmark = {
      ...bookmark,
      scrollPosition,
      scrollPercentage,
      timestamp: new Date().toISOString(),
    };

    setBookmark(updatedBookmark);
    setStorageItem(STORAGE_KEYS.bookmark, updatedBookmark);
  }, [bookmark]);

  /**
   * Clear bookmark
   */
  const clearBookmark = useCallback(() => {
    setBookmark(null);
    removeStorageItem(STORAGE_KEYS.bookmark);
  }, []);

  /**
   * Check if a specific chapter is bookmarked
   * @param {string} chapterId - Chapter ID to check
   * @returns {boolean} Is chapter bookmarked
   */
  const isChapterBookmarked = useCallback((chapterId) => {
    return bookmark?.chapterId === chapterId;
  }, [bookmark]);

  /**
   * Mark a chapter as visited
   * @param {string} chapterId - Chapter ID to mark
   */
  const markChapterVisited = useCallback((chapterId) => {
    setVisitedChapters(prev => {
      if (prev.includes(chapterId)) return prev;
      
      const updated = [...prev, chapterId];
      setStorageItem(STORAGE_KEYS.visitedChapters, updated);
      return updated;
    });
  }, []);

  /**
   * Check if a chapter has been visited
   * @param {string} chapterId - Chapter ID to check
   * @returns {boolean} Has chapter been visited
   */
  const isChapterVisited = useCallback((chapterId) => {
    return visitedChapters.includes(chapterId);
  }, [visitedChapters]);

  /**
   * Clear all visited chapters
   */
  const clearVisitedChapters = useCallback(() => {
    setVisitedChapters([]);
    removeStorageItem(STORAGE_KEYS.visitedChapters);
  }, []);

  /**
   * Clear all reading data (bookmark + visited)
   */
  const clearAllReadingData = useCallback(() => {
    clearBookmark();
    clearVisitedChapters();
  }, [clearBookmark, clearVisitedChapters]);

  /**
   * Get formatted bookmark info for display
   * @returns {Object|null} Formatted bookmark info
   */
  const getBookmarkDisplay = useCallback(() => {
    if (!bookmark) return null;

    const date = new Date(bookmark.timestamp);
    const timeAgo = getTimeAgo(date);

    return {
      chapterId: bookmark.chapterId,
      chapterTitle: bookmark.chapterTitle,
      progress: `${Math.round(bookmark.scrollPercentage)}%`,
      timeAgo,
      fullDate: date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  }, [bookmark]);

  /**
   * Get reading statistics
   * @param {number} totalChapters - Total number of chapters
   * @returns {Object} Reading stats
   */
  const getReadingStats = useCallback((totalChapters) => {
    const chaptersRead = visitedChapters.length;
    const percentage = totalChapters > 0 
      ? Math.round((chaptersRead / totalChapters) * 100) 
      : 0;

    return {
      chaptersRead,
      totalChapters,
      percentage,
      isComplete: chaptersRead >= totalChapters,
      remainingChapters: Math.max(0, totalChapters - chaptersRead),
    };
  }, [visitedChapters]);

  /**
   * Check if user has any reading progress
   * @returns {boolean} Has reading progress
   */
  const hasReadingProgress = useCallback(() => {
    return bookmark !== null || visitedChapters.length > 0;
  }, [bookmark, visitedChapters]);

  return {
    // State
    bookmark,
    visitedChapters,
    isLoading,

    // Bookmark actions
    saveBookmark,
    updateScrollPosition,
    clearBookmark,
    isChapterBookmarked,

    // Visited chapters actions
    markChapterVisited,
    isChapterVisited,
    clearVisitedChapters,

    // Combined actions
    clearAllReadingData,
    hasReadingProgress,

    // Display helpers
    getBookmarkDisplay,
    getReadingStats,
  };
};

/**
 * Helper function to get human-readable time ago string
 * @param {Date} date - Date to compare
 * @returns {string} Time ago string
 */
function getTimeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'week', seconds: 604800 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
    }
  }

  return 'Just now';
}

export default useBookmark;
